import type { WeatherSnapshot, SceneConfig, SkyGradient, Visibility } from '@/types/weather';

function sky(top: string, bottom: string): SkyGradient {
  return { top, bottom };
}

// ─── Sky palettes ─────────────────────────────────────────────────────────────

const SKY = {
  clearDay:        sky('#3388CC', '#88CCEE'),
  clearNight:      sky('#0E1252', '#1A1F88'),
  mainlyClearDay:  sky('#4499DD', '#99DDFF'),
  mainlyClearNight:sky('#101455', '#1C2278'),
  partlyDay:       sky('#5588BB', '#99BBDD'),
  partlyNight:     sky('#131A66', '#222E99'),
  overcastDay:     sky('#334499', '#4455CC'),
  overcastNight:   sky('#0E112E', '#1A2044'),
  fogDay:          sky('#7788BB', '#AABBD4'),
  fogNight:        sky('#25305E', '#3A4480'),
  rainDay:         sky('#1A2299', '#2A33CC'),
  rainNight:       sky('#080A22', '#12163A'),
  heavyRainDay:    sky('#0A0E3D', '#14196A'),
  heavyRainNight:  sky('#050818', '#0A0E30'),
  snowDay:         sky('#2244AA', '#4466CC'),
  snowNight:       sky('#0A1044', '#141A66'),
  stormDay:        sky('#0A0E28', '#151840'),
  stormNight:      sky('#050610', '#0A0B1A'),
};

// ─── Wind helpers ─────────────────────────────────────────────────────────────

function cloudDriftFromWind(windDirection: number): 'left' | 'right' {
  const eastComponent = -Math.sin((windDirection * Math.PI) / 180);
  return eastComponent >= 0 ? 'right' : 'left';
}

function rainAngleFromWind(windDirection: number, windSpeed: number): number {
  const eastComponent = -Math.sin((windDirection * Math.PI) / 180);
  const raw = eastComponent * Math.min(windSpeed / 25, 1.5) * 22;
  return Math.max(-35, Math.min(35, raw));
}

function visibilityLevel(code: number, visibilityMetres: number): Visibility {
  if (code === 45 || code === 48) return 'fog';
  if (visibilityMetres < 5000) return 'fog';
  if (visibilityMetres < 10000) return 'haze';
  return 'clear';
}

// ─── Scene config ─────────────────────────────────────────────────────────────

export function getSceneConfig(weather: WeatherSnapshot): SceneConfig {
  const { weatherCode: code, isDay, windSpeed, windDirection, visibility } = weather;

  const windMultiplier = Math.max(0.25, 1 - windSpeed / 80);
  const cloudDriftDirection = cloudDriftFromWind(windDirection);
  const rainAngle = rainAngleFromWind(windDirection, windSpeed);
  const fogLevel = visibilityLevel(code, visibility);

  // WMO 0 — Clear sky
  if (code === 0) {
    return {
      skyGradient: isDay ? SKY.clearDay : SKY.clearNight,
      cloudDensity: 'none', cloudStyle: 'light',
      sunOpacity: isDay ? 1 : 0, moonOpacity: isDay ? 0 : 1, showStars: !isDay,
      precipitation: 'none', precipitationIntensity: 0,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 1 — Mainly clear
  if (code === 1) {
    return {
      skyGradient: isDay ? SKY.mainlyClearDay : SKY.mainlyClearNight,
      cloudDensity: 'sparse', cloudStyle: 'light',
      sunOpacity: isDay ? 1 : 0, moonOpacity: isDay ? 0 : 1, showStars: !isDay,
      precipitation: 'none', precipitationIntensity: 0,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 2 — Partly cloudy
  if (code === 2) {
    return {
      skyGradient: isDay ? SKY.partlyDay : SKY.partlyNight,
      cloudDensity: 'scattered', cloudStyle: 'light',
      sunOpacity: isDay ? 1 : 0, moonOpacity: isDay ? 0 : 0.7, showStars: !isDay,
      precipitation: 'none', precipitationIntensity: 0,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 3 — Overcast
  if (code === 3) {
    return {
      skyGradient: isDay ? SKY.overcastDay : SKY.overcastNight,
      cloudDensity: 'overcast', cloudStyle: 'dark',
      sunOpacity: isDay ? 0.25 : 0, moonOpacity: isDay ? 0 : 0.2, showStars: false,
      precipitation: 'none', precipitationIntensity: 0,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 45, 48 — Fog
  if (code === 45 || code === 48) {
    return {
      skyGradient: isDay ? SKY.fogDay : SKY.fogNight,
      cloudDensity: 'overcast', cloudStyle: 'light',
      sunOpacity: 0, moonOpacity: isDay ? 0 : 0.15, showStars: false,
      precipitation: 'none', precipitationIntensity: 0,
      fog: 'fog', lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 51–55 — Drizzle
  if (code >= 51 && code <= 55) {
    const intensity = code <= 51 ? 0.25 : code <= 53 ? 0.45 : 0.65;
    return {
      skyGradient: isDay ? SKY.rainDay : SKY.rainNight,
      cloudDensity: 'scattered', cloudStyle: 'dark',
      sunOpacity: 0, moonOpacity: isDay ? 0 : 0.25, showStars: false,
      precipitation: 'drizzle', precipitationIntensity: intensity,
      fog: fogLevel !== 'fog' ? 'haze' : 'fog',
      lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 56–57 — Freezing drizzle
  if (code === 56 || code === 57) {
    return {
      skyGradient: isDay ? SKY.rainDay : SKY.rainNight,
      cloudDensity: 'overcast', cloudStyle: 'dark',
      sunOpacity: 0, moonOpacity: isDay ? 0 : 0.15, showStars: false,
      precipitation: 'sleet', precipitationIntensity: 0.5,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 61–65 — Rain
  if (code >= 61 && code <= 65) {
    const intensity = code === 61 ? 0.35 : code === 63 ? 0.65 : 1.0;
    const heavy = code === 65;
    return {
      skyGradient: isDay
        ? (heavy ? SKY.heavyRainDay : SKY.rainDay)
        : (heavy ? SKY.heavyRainNight : SKY.rainNight),
      cloudDensity: 'overcast', cloudStyle: heavy ? 'stormy' : 'dark',
      sunOpacity: 0, moonOpacity: isDay ? 0 : (heavy ? 0 : 0.15), showStars: false,
      precipitation: heavy ? 'heavy-rain' : 'rain', precipitationIntensity: intensity,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 66–67 — Freezing rain / sleet
  if (code === 66 || code === 67) {
    return {
      skyGradient: isDay ? SKY.rainDay : SKY.rainNight,
      cloudDensity: 'overcast', cloudStyle: 'dark',
      sunOpacity: 0, moonOpacity: isDay ? 0 : 0.15, showStars: false,
      precipitation: 'sleet', precipitationIntensity: code === 67 ? 0.8 : 0.5,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 71–77 — Snow
  if (code >= 71 && code <= 77) {
    const intensity = code === 71 ? 0.3 : code === 73 ? 0.6 : 1.0;
    return {
      skyGradient: isDay ? SKY.snowDay : SKY.snowNight,
      cloudDensity: 'overcast', cloudStyle: 'light',
      sunOpacity: 0, moonOpacity: isDay ? 0 : 0.2, showStars: false,
      precipitation: 'snow', precipitationIntensity: intensity,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 80–82 — Rain showers
  if (code >= 80 && code <= 82) {
    const intensity = code === 80 ? 0.5 : code === 81 ? 0.75 : 1.0;
    return {
      skyGradient: isDay ? SKY.heavyRainDay : SKY.heavyRainNight,
      cloudDensity: 'overcast', cloudStyle: 'stormy',
      sunOpacity: 0, moonOpacity: 0, showStars: false,
      precipitation: code === 82 ? 'heavy-rain' : 'rain', precipitationIntensity: intensity,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 85–86 — Snow showers
  if (code === 85 || code === 86) {
    return {
      skyGradient: isDay ? SKY.snowDay : SKY.snowNight,
      cloudDensity: 'overcast', cloudStyle: 'dark',
      sunOpacity: 0, moonOpacity: isDay ? 0 : 0.15, showStars: false,
      precipitation: 'snow', precipitationIntensity: code === 86 ? 0.9 : 0.5,
      fog: fogLevel, lightning: false, lightningFrequency: 'none',
      windMultiplier, cloudDriftDirection, rainAngle,
    };
  }

  // WMO 95 — Thunderstorm
  if (code === 95) {
    return {
      skyGradient: isDay ? SKY.stormDay : SKY.stormNight,
      cloudDensity: 'overcast', cloudStyle: 'stormy',
      sunOpacity: 0, moonOpacity: 0, showStars: false,
      precipitation: 'heavy-rain', precipitationIntensity: 0.9,
      fog: fogLevel, lightning: true, lightningFrequency: 'occasional',
      windMultiplier: Math.min(windMultiplier, 0.5), cloudDriftDirection,
      rainAngle,
    };
  }

  // WMO 96, 99 — Thunderstorm with hail
  if (code === 96 || code === 99) {
    return {
      skyGradient: isDay ? SKY.stormDay : SKY.stormNight,
      cloudDensity: 'overcast', cloudStyle: 'stormy',
      sunOpacity: 0, moonOpacity: 0, showStars: false,
      precipitation: 'heavy-rain', precipitationIntensity: 1.0,
      fog: fogLevel, lightning: true, lightningFrequency: 'frequent',
      windMultiplier: Math.min(windMultiplier, 0.4), cloudDriftDirection,
      rainAngle,
    };
  }

  // Fallback
  return {
    skyGradient: isDay ? SKY.overcastDay : SKY.overcastNight,
    cloudDensity: 'scattered', cloudStyle: 'dark',
    sunOpacity: 0, moonOpacity: isDay ? 0 : 0.2, showStars: false,
    precipitation: 'none', precipitationIntensity: 0,
    fog: fogLevel, lightning: false, lightningFrequency: 'none',
    windMultiplier, cloudDriftDirection, rainAngle,
  };
}

export const WMO_DESCRIPTIONS: Record<number, string> = {
  0: 'Clear Sky', 1: 'Mainly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Icy Fog',
  51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
  56: 'Freezing Drizzle', 57: 'Heavy Freezing Drizzle',
  61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain',
  66: 'Freezing Rain', 67: 'Heavy Freezing Rain',
  71: 'Light Snow', 73: 'Snow', 75: 'Heavy Snow', 77: 'Snow Grains',
  80: 'Rain Showers', 81: 'Heavy Showers', 82: 'Violent Showers',
  85: 'Snow Showers', 86: 'Heavy Snow Showers',
  95: 'Thunderstorm', 96: 'Thunderstorm with Hail', 99: 'Thunderstorm with Heavy Hail',
};
